import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, Check, Users, Clock, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api, getSessionId } from '../config/api';
import './LobbyPage.css';

function LobbyPage() {
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lobby, setLobby] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);

  const fetchLobby = useCallback(async () => {
    try {
      const data = await api.getLobby(lobbyId);
      setLobby(data.lobby);
      
      if (data.lobby.status === 'started' && data.lobby.gameId) {
        navigate(`/games/guesswhat/${data.lobby.gameId}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [lobbyId, navigate]);

  useEffect(() => {
    fetchLobby();
    
    const interval = setInterval(fetchLobby, 3000);
    return () => clearInterval(interval);
  }, [fetchLobby]);

  const copyJoinCode = async () => {
    if (!lobby?.joinCode) return;
    
    try {
      await navigator.clipboard.writeText(lobby.joinCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleStartGame = async () => {
    setStarting(true);
    setError(null);
    try {
      const result = await api.startGame(lobbyId);
      navigate(`/games/guesswhat/${result.gameId}`);
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="lobby-page">
        <div className="container">
          <div className="loading">Loading lobby...</div>
        </div>
      </div>
    );
  }

  if (error && !lobby) {
    return (
      <div className="lobby-page">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  const effectiveUid = user?.uid || `anon_${getSessionId()}`;
  const isHost = lobby?.hostUid === effectiveUid;
  const memberCount = lobby?.members?.length || 0;
  const canStart = memberCount >= 2;

  return (
    <div className="lobby-page">
      <div className="container">
        <div className="lobby-card">
          <div className="lobby-header">
            <h1>Guess What Lobby</h1>
            <span className={`lobby-status status-${lobby?.status}`}>
              {lobby?.status === 'open' ? 'Waiting for players' : lobby?.status}
            </span>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="lobby-invite">
            <h2>Invite a Friend</h2>
            <div className="invite-methods">
              <div className="invite-method">
                <label>Join Code</label>
                <div className="code-display">
                  <span className="join-code">{lobby?.joinCode}</span>
                  <button onClick={copyJoinCode} className="copy-button">
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
              <div className="invite-method">
                <label>Or share this link</label>
                <button onClick={copyLink} className="copy-link-button">
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          </div>

          <div className="lobby-players">
            <h2>
              <Users size={20} />
              Players ({memberCount}/2)
            </h2>
            <ul className="players-list">
              {lobby?.members?.map((member, index) => (
                <li key={member.uid} className="player-item">
                  <span className="player-name">
                    {member.displayName}
                    {member.uid === lobby.hostUid && <span className="host-badge">Host</span>}
                  </span>
                  <span className="player-status">
                    {member.ready ? <Check size={16} /> : <Clock size={16} />}
                  </span>
                </li>
              ))}
              {memberCount < 2 && (
                <li className="player-item empty">
                  <span className="player-name">Waiting for player...</span>
                </li>
              )}
            </ul>
          </div>

          {isHost && (
            <div className="lobby-actions">
              <button 
                className="start-button"
                onClick={handleStartGame}
                disabled={!canStart || starting}
              >
                <Play size={20} />
                {starting ? 'Starting...' : 'Start Game'}
              </button>
              {!canStart && (
                <p className="start-hint">Need 2 players to start</p>
              )}
            </div>
          )}

          {!isHost && (
            <div className="lobby-waiting">
              <p>Waiting for host to start the game...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LobbyPage;
